import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '../context/ThemeContext';

function levenshtein(a, b) {
    const m = a.length, n = b.length;
    const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
    }
    return dp[m][n];
}

function normalize(str) { return (str || '').trim().toLowerCase().normalize('NFC'); }

export function getProximityFeedback(inputRaw, answerRaw) {
    const input = normalize(inputRaw);
    const answer = normalize(answerRaw);
    if (!input || input === answer) return null;
    const dist = levenshtein(input, answer);
    const minLen = Math.min(input.length, answer.length);
    if (minLen < 3) return null;
    if (dist === 1) return { level: 'veryClose', message: '🔥 Heel dichtbij! Nog één lettertje.' };
    if (dist === 2 && answer.length > 4) return { level: 'close', message: '👀 Bijna goed! Twee letters verschil.' };
    return null;
}

export default function LetterSlotInput({ answer = '', value = '', onChange, onSubmit, autoFocus = false, disabled = false }) {
    const hiddenRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const slots = answer.length || 1;
    const isRtl = /[\u0600-\u06FF]/.test(answer);
    const chars = value.split('');

    const emptyBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.5)';
    const emptyBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(80,70,140,0.2)';
    const filledBg = isDark ? 'rgba(108,99,255,0.12)' : 'rgba(85,72,224,0.1)';
    const filledBorder = isDark ? 'rgba(108,99,255,0.4)' : 'rgba(85,72,224,0.4)';
    const charColor = isDark ? '#e8e8f0' : '#1a1830';
    const bottomLineColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(80,70,140,0.2)';

    useEffect(() => { if (autoFocus && hiddenRef.current) hiddenRef.current.focus(); }, [autoFocus, answer]);

    return (
        <Box onClick={() => { if (!disabled) hiddenRef.current?.focus(); }} sx={{ display: 'flex', flexDirection: isRtl ? 'row-reverse' : 'row', gap: { xs: '6px', sm: '8px' }, flexWrap: 'wrap', justifyContent: 'center', cursor: disabled ? 'default' : 'text', py: 1, position: 'relative' }}>
            <input ref={hiddenRef} value={value} onChange={(e) => onChange?.(e.target.value.slice(0, slots))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit?.(); } }} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} disabled={disabled} dir={isRtl ? 'rtl' : 'ltr'} inputMode="text" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: '100%', height: '100%', top: 0, left: 0, fontSize: 16 }} />

            {Array.from({ length: slots }).map((_, i) => {
                const char = chars[i] || '';
                const isActive = !disabled && i === chars.length && isFocused;
                const isFilled = !!char;

                return (
                    <Box key={i} sx={{
                        width: { xs: 36, sm: 42 }, height: { xs: 48, sm: 56 }, borderRadius: '10px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                        background: isFilled ? filledBg : emptyBg,
                        border: isActive ? '2px solid #6c63ff' : isFilled ? `1px solid ${filledBorder}` : `1px solid ${emptyBorder}`,
                        transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
                        boxShadow: isActive ? '0 0 0 3px rgba(108,99,255,0.18)' : 'none',
                    }}>
                        {isActive && !char && (
                            <Box sx={{ width: 2, height: 24, background: '#6c63ff', borderRadius: 1, animation: 'blink 1s step-end infinite', '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } } }} />
                        )}
                        {char && (
                            <Typography sx={{ color: charColor, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1, userSelect: 'none' }}>
                                {char}
                            </Typography>
                        )}
                        {!char && !isActive && (
                            <Box sx={{ position: 'absolute', bottom: 6, left: '20%', right: '20%', height: 2, borderRadius: 1, background: bottomLineColor }} />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
